/**
 * Read an image file and resize it to a WebP data URL bounded by `max` on its
 * longest side, so what we upload stays tiny regardless of the source photo.
 * Shared by the furniture catalog and the profile avatar picker.
 */
export async function fileToResizedDataUrl(file: File, max = 256, quality = 0.82): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('Could not read that file.'))
    reader.readAsDataURL(file)
  })
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('That file is not a readable image.'))
    image.src = dataUrl
  })
  const scale = Math.min(1, max / Math.max(img.width, img.height))
  const width = Math.max(1, Math.round(img.width * scale))
  const height = Math.max(1, Math.round(img.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Image resizing is not supported in this browser.')
  ctx.drawImage(img, 0, 0, width, height)
  return canvas.toDataURL('image/webp', quality)
}
